import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

// ── Button ─────────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-400",
  secondary:
    "bg-slate-800 text-slate-100 hover:bg-slate-700 border border-slate-700 focus-visible:outline-slate-400",
  danger: "bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-400",
  ghost: "bg-transparent text-slate-300 hover:bg-slate-800 focus-visible:outline-slate-400",
};

export function Button({
  variant = "primary",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        BUTTON_VARIANTS[variant],
        className,
      )}
    />
  );
}

// ── Inputs ─────────────────────────────────────────────────────────────────

const FIELD_CLASS =
  "w-full scroll-mt-20 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 " +
  "placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none disabled:opacity-50";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(FIELD_CLASS, className)} />;
}

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(FIELD_CLASS, "pr-8", className)} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cn(FIELD_CLASS, "min-h-20", className)} />;
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block space-y-1", className)}>
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-slate-400">{hint}</span> : null}
    </label>
  );
}

export function Checkbox({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-200">
      <input
        type="checkbox"
        {...props}
        className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
      />
      {label}
    </label>
  );
}

export function AutocompleteInput({ value, onChange, options, placeholder }: { value:string; onChange:(value:string)=>void; options:string[]; placeholder?:string }) {
  const id = useId();
  const unique = Array.from(new Set(options.filter(Boolean))).sort((a,b)=>a.localeCompare(b));
  return <><Input list={id} value={value} onChange={(event)=>onChange(event.target.value)} placeholder={placeholder}/><datalist id={id}>{unique.map(option=><option key={option} value={option}/>)}</datalist></>;
}

// ── Surfaces ───────────────────────────────────────────────────────────────

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("rounded-lg border border-slate-800 bg-slate-900/60 p-4", className)}>
      {children}
    </div>
  );
}

/**
 * Collapsible form section. Sections that are rarely used start collapsed so
 * the editor does not require a long scroll.
 */
export function Section({
  title,
  hint,
  defaultOpen = true,
  children,
}: {
  title: string;
  hint?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/60">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/40"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-slate-200">{title}</span>
          {hint ? <span className="text-xs text-slate-400">{hint}</span> : null}
        </span>
        <span className="text-slate-400">{open ? "\u2212" : "+"}</span>
      </button>
      {open ? (
        <div className="space-y-3 border-t border-slate-800 px-4 py-3">{children}</div>
      ) : null}
    </div>
  );
}

type BadgeTone = "neutral" | "green" | "amber" | "red" | "indigo";

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "bg-slate-800 text-slate-300 border-slate-700",
  green: "bg-emerald-950 text-emerald-300 border-emerald-800",
  amber: "bg-amber-950 text-amber-300 border-amber-800",
  red: "bg-red-950 text-red-300 border-red-800",
  indigo: "bg-indigo-950 text-indigo-300 border-indigo-800",
};

export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element?.showModal();
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId} aria-modal="true"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]'))
          .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}
      className="m-0 h-dvh max-h-none w-dvw max-w-none border-0 bg-transparent p-0 text-slate-100 backdrop:bg-black/60">
    <div className="flex h-full items-end justify-center overflow-y-auto sm:items-center sm:p-4 md:p-8">
      <div
        className={cn(
          "max-h-[92dvh] w-full overflow-y-auto rounded-t-xl border border-slate-800 bg-slate-950 shadow-2xl sm:rounded-xl",
          wide ? "max-w-4xl" : "max-w-lg",
        )}
      >
        <div className="sticky top-0 flex items-center justify-between gap-3 border-b border-slate-800 bg-slate-950 px-4 py-3 sm:px-5">
          <h2 id={titleId} className="min-w-0 text-base font-semibold text-slate-100">{title}</h2>
          <Button type="button" variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>
        <div className="px-4 py-4 sm:px-5">{children}</div>
      </div>
    </div>
    </dialog>, document.body,
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return <div className="py-10 text-center text-sm text-slate-400">{label}</div>;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="py-12 text-center">
      <p className="text-sm font-medium text-slate-300">{title}</p>
      {hint ? <p className="mt-1 text-sm text-slate-400">{hint}</p> : null}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-md border border-red-800 bg-red-950/60 px-3 py-2 text-sm text-red-200">
      {message}
    </div>
  );
}
